// A project's immovable goals (Brief 4 / P8) — the load+save state for the
// alignment anchor. The DATABASE is the home: the backend stores the markdown
// document per project (`GET/PUT /projects/{id}/goals`) and this hook loads it,
// holds the saving state, and surfaces a single error. Unlike the second brain
// (derived from a session's Trace), these goals are AUTHORED — the highest-level
// context the orchestrator establishes and the sessions must stay locked to.

import { useCallback, useEffect, useRef, useState } from "react";
import { ApiError, getProjectGoals, putProjectGoals } from "../../api/client";

export interface UseProjectGoals {
  /** The goals markdown ("" when unset). */
  markdown: string;
  /** Server micros since unix epoch of the last save; 0 when never set. */
  updatedAt: number;
  loading: boolean;
  saving: boolean;
  /** A load/save error to surface ("" when none). */
  error: string;
  /** Author/replace the goals; resolves on success, rejects on failure. */
  save: (markdown: string) => Promise<void>;
}

export function useProjectGoals(projectId: number): UseProjectGoals {
  const [markdown, setMarkdown] = useState("");
  const [updatedAt, setUpdatedAt] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const aliveRef = useRef(true);

  useEffect(() => {
    aliveRef.current = true;
    if (!projectId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");
    getProjectGoals(projectId)
      .then((r) => {
        if (!aliveRef.current) return;
        setMarkdown(r.markdown);
        setUpdatedAt(r.updated_at);
        setLoading(false);
      })
      .catch((e) => {
        if (!aliveRef.current) return;
        setError(
          e instanceof ApiError ? e.message : "Could not load the project goals",
        );
        setLoading(false);
      });
    return () => {
      aliveRef.current = false;
    };
  }, [projectId]);

  const save = useCallback(
    async (next: string) => {
      setSaving(true);
      setError("");
      try {
        const r = await putProjectGoals(projectId, next);
        if (!aliveRef.current) return;
        setMarkdown(r.markdown);
        setUpdatedAt(r.updated_at);
      } catch (e) {
        if (aliveRef.current) {
          setError(
            e instanceof ApiError
              ? e.message
              : "Could not save the project goals",
          );
        }
        throw e;
      } finally {
        if (aliveRef.current) setSaving(false);
      }
    },
    [projectId],
  );

  return { markdown, updatedAt, loading, saving, error, save };
}
